import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureFlagService } from '@galaxy/partner';
import { BehaviorSubject, EMPTY, Observable, Subscription, combineLatest, of as observableOf } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AccessService, Feature, StubAccessService } from '../core/access';
import { MarketsService } from '../core/markets/markets.service';
import { FEATURE_BULK_ACTIONS_V2 } from '../flags';
import { ActionList } from './action-list';
import { ActionListsApiService } from './action-lists.api.service';
import { PagedResponse } from './action-lists.api.service.response';
import {
  Action,
  ActionsApiService,
  ActivateItemDialog,
  AddTagsDialog,
  ChangeMarketDialog,
  CreateBrandReportDialog,
  CreateRefreshSnapshotDialog,
  ExportAccountDataDialog,
  PauseCampaignDialog,
} from './actions';
import { SelectionType } from './actions/activate-item-dialog.component';
import { AddToCampaignDialogComponent } from './actions/add-to-campaign-dialog.component';
import { StartAutomationDialogComponent } from './actions/start-automation-dialog.component';

export class ActionListsNotification {
  text: string;
  error: boolean;

  constructor(text: string, error: boolean) {
    this.text = text;
    this.error = error;
  }
}

export interface ActionListFetcher {
  getAvailableActions(id: string): Observable<Action[]>;

  getActionList(actionListId: string): Observable<ActionList>;

  deleteActionList(actionList: ActionList, redirect: boolean): void;
}

@Injectable({ providedIn: 'root' })
export class ActionListsStoreService implements ActionListFetcher {
  private actionListsSubject$$: BehaviorSubject<ActionList[]> = new BehaviorSubject(null);
  private nextPageSubject$$: BehaviorSubject<number> = new BehaviorSubject(0);
  private hasMoreSubject$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private totalActionListsCountSubject$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private loadingMoreSubject$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private searchSubject$$: BehaviorSubject<string> = new BehaviorSubject(null);
  private canCreateOrders$: Observable<boolean> = this.partnerId$.pipe(
    switchMap((partnerId) => this.featureFlagService.batchGetStatus(partnerId, '', ['no_admin_order_creation'])),
    map((featureFlagStatus) => featureFlagStatus['no_admin_order_creation']),
    shareReplay(1),
  );

  private notification$$: BehaviorSubject<ActionListsNotification> = new BehaviorSubject(null);
  private deletedActionListIds: string[] = [];
  private subscriptions: Subscription[] = [];
  private accessibleMarkets: string[];

  constructor(
    private actionListsApiService: ActionListsApiService,
    private actionsApiService: ActionsApiService,
    private router: Router,
    private marketsService: MarketsService,
    private featureFlagService: FeatureFlagService,
    @Inject(StubAccessService) private accessService: AccessService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    @Inject(FEATURE_BULK_ACTIONS_V2) private readonly useBulkActionsV2$: Observable<boolean>,
  ) {}

  get notification$(): Observable<ActionListsNotification> {
    return this.notification$$.asObservable();
  }

  get actionLists$(): Observable<ActionList[]> {
    return this.actionListsSubject$$.asObservable();
  }

  get hasMore$(): Observable<boolean> {
    return this.hasMoreSubject$$.asObservable();
  }

  get totalActionListsCount$(): Observable<number> {
    return this.totalActionListsCountSubject$$.asObservable();
  }

  get loadingMore$(): Observable<boolean> {
    return this.loadingMoreSubject$$.asObservable();
  }

  get search$(): Observable<string> {
    return this.searchSubject$$.asObservable();
  }

  get nextPage$(): Observable<number> {
    return this.nextPageSubject$$.asObservable();
  }

  public initializeStore(initialSize?: number): void {
    this.actionListsSubject$$.next(null);
    this.notification$$ = new BehaviorSubject<ActionListsNotification>(null);
    this.subscriptions.push(
      this.marketsService.markets
        .pipe(
          switchMap((markets) => {
            this.accessibleMarkets = markets.map((market) => market.market_id);
            return this.actionListsApiService.lookupActionLists(0, this.accessibleMarkets, initialSize).pipe(
              catchError((err) => {
                this.notification$$.next(this.buildNotification(err.message, true));
                return observableOf(null);
              }),
              map((response) => this.handleActionListsResponse(response)),
            );
          }),
        )
        .subscribe(),
    );
  }

  public updateSearch(search: string): Observable<void> {
    this.actionListsSubject$$.next(null);
    this.totalActionListsCountSubject$$.next(0);
    this.loadingMoreSubject$$.next(true);
    this.hasMoreSubject$$.next(false);
    this.searchSubject$$.next(search);
    return this.actionListsApiService.lookupActionLists(0, this.accessibleMarkets, undefined, search).pipe(
      take(1),
      finalize(() => this.loadingMoreSubject$$.next(false)),
      map((response) => this.handleActionListsResponse(response)),
      catchError(() => {
        this.notification$$.next(this.buildNotification('Failed to load lists', true));
        return EMPTY;
      }),
    );
  }

  public loadMoreActionLists(): void {
    this.loadingMoreSubject$$.next(true);
    this.hasMoreSubject$$.next(false);
    this.actionListsApiService
      .lookupActionLists(
        this.nextPageSubject$$.getValue(),
        this.accessibleMarkets,
        undefined,
        this.searchSubject$$.getValue() || undefined,
      )
      .pipe(
        take(1),
        finalize(() => this.loadingMoreSubject$$.next(false)),
      )
      .subscribe(
        (response) => this.handleActionListsResponse(response),
        () => this.notification$$.next(this.buildNotification('Failed to load lists', true)),
      );
  }

  public deleteActionList(actionList: ActionList, redirect = false): void {
    this.actionListsApiService.deleteActionList(actionList.id).subscribe(
      () => {
        this.notification$$.next(this.buildNotification(`${actionList.name} has been deleted successfully`, false));
        this.deletedActionListIds.push(actionList.id);
        const actionLists = this.actionListsSubject$$.getValue();
        if (actionLists) {
          this.actionListsSubject$$.next(actionLists.filter((al) => al.id !== actionList.id));
        }
        if (redirect) {
          this.router.navigateByUrl('/action-lists/manage');
        }
      },
      (error) => {
        if (error.statusCode === 404) {
          this.notification$$.next(this.buildNotification(`${actionList.name} could not be found`, true));
        } else {
          this.notification$$.next(this.buildNotification('Failed to delete list. Please try again.', true));
        }
      },
    );
  }

  public createActionList(listName: string): void {
    this.subscriptions.push(
      this.actionListsApiService.createActionList(listName).subscribe({
        next: (actionList) => {
          this.notification$$.next(this.buildNotification(`${listName} has been created successfully`, false));
          const currentActionLists = (this.actionListsSubject$$.getValue() || []).slice();
          currentActionLists.unshift(actionList);
          this.actionListsSubject$$.next(currentActionLists);
        },
        error: () => {
          this.notification$$.next(
            this.buildNotification(`Failed to create list ${listName}. Please try again.`, true),
          );
        },
      }),
    );
  }

  public getAvailableActions(): Observable<Action[]> {
    return combineLatest([
      this.actionListsApiService.getRestrictions(),
      this.accessService.hasAccessToFeature(Feature.customCampaigns),
      this.accessService.hasAccessToFeature(Feature.markets),
      this.accessService.hasAccessToFeature(Feature.brands),
      this.accessService.hasAccessToFeature(Feature.snapshotBulkActivations),
      this.accessService.hasAccessToFeature(Feature.advancedAutomationTasks),
      this.useBulkActionsV2$,
      this.canCreateOrders$,
    ]).pipe(
      map(
        ([
          restrictions,
          hasAccessToCampaigns,
          hasAccessToMarkets,
          hasAccessToBrands,
          hasAccessToBulkSnapshot,
          hasAccessToAdvancedAutomationTasks,
          useNewWorkflow,
          canCreateOrders,
        ]: [any, boolean, boolean, boolean, boolean, boolean, boolean, boolean]) => {
          const canCreateBrands = restrictions.allowCreateBrand && hasAccessToBrands;
          const allowedActions: Action[] = [
            {
              name: 'Export Account Data',
              dialog: ExportAccountDataDialog,
              useBulkActionsMicroservice: useNewWorkflow,
            },
            { name: 'Add Tags', dialog: AddTagsDialog, useBulkActionsMicroservice: useNewWorkflow },
            {
              name: 'Add to Campaign',
              dialog: AddToCampaignDialogComponent,
              featureId: Feature.marketingAutomation,
              locked: !hasAccessToCampaigns,
              useBulkActionsMicroservice: useNewWorkflow,
            },
            {
              name: 'Pause Campaign',
              dialog: PauseCampaignDialog,
              featureId: Feature.marketingAutomation,
              locked: !hasAccessToCampaigns,
              useBulkActionsMicroservice: useNewWorkflow,
            },
            {
              name: 'Create Multi-Location Group',
              dialog: CreateBrandReportDialog,
              featureId: Feature.brands,
              locked: !canCreateBrands,
              useBulkActionsMicroservice: useNewWorkflow,
            },
            {
              name: 'Create/Refresh Snapshot',
              dialog: CreateRefreshSnapshotDialog,
              featureId: Feature.snapshotBulkActivations,
              locked: !hasAccessToBulkSnapshot,
              useBulkActionsMicroservice: useNewWorkflow,
            },
          ];
          allowedActions.push(
            // Starting an automation from a list doesnt actually use AA or bulk-actions µs to
            // perform the action. Instead it just uses the bulk-actions µs to list the items and processes them in
            // automata µs
            {
              name: 'Trigger automation',
              dialog: StartAutomationDialogComponent,
              featureId: Feature.advancedAutomationTasks,
              locked: !hasAccessToAdvancedAutomationTasks,
              useBulkActionsMicroservice: useNewWorkflow,
            },
          );
          if (restrictions.allowActivateProduct && canCreateOrders) {
            allowedActions.push(
              {
                name: 'Activate Product',
                dialog: ActivateItemDialog,
                data: {
                  selectionType: SelectionType.PRODUCT,
                },
                useBulkActionsMicroservice: useNewWorkflow,
              },
              {
                name: 'Activate Addon',
                dialog: ActivateItemDialog,
                data: {
                  selectionType: SelectionType.ADDON,
                },
                useBulkActionsMicroservice: useNewWorkflow,
              },
            );
          }
          if (restrictions.allowChangeMarket && hasAccessToMarkets) {
            allowedActions.push({
              name: 'Change Market',
              dialog: ChangeMarketDialog,
              useBulkActionsMicroservice: useNewWorkflow,
            });
          }

          return allowedActions;
        },
      ),
    );
  }

  public getActionList(actionListId: string): Observable<ActionList> {
    return this.actionListsApiService.getActionList(actionListId).pipe(
      map((result) => {
        return new ActionList(result);
      }),
    );
  }

  private handleActionListsResponse(response: PagedResponse): void {
    if (response) {
      const currentActionLists = this.actionListsSubject$$.getValue() || [];
      let newActionLists = response.actionLists;
      if (this.deletedActionListIds.length > 0) {
        newActionLists = newActionLists.filter((al) => this.deletedActionListIds.indexOf(al.id) < 0);
      }
      this.actionListsSubject$$.next(currentActionLists.concat(newActionLists));
      this.hasMoreSubject$$.next(response.hasMore);
      this.nextPageSubject$$.next(response.nextPage);
      this.totalActionListsCountSubject$$.next(response.totalActionListsCount);
    }
  }

  private buildNotification(text: string, error: boolean): ActionListsNotification {
    return new ActionListsNotification(text, error);
  }

  public destroyStore(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.notification$$.complete();
  }
}
